import { useMemo, useRef, useCallback, useEffect } from 'react';
import type { ColDef, GridApi, ValueFormatterParams, SortModelItem } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from '@/components/ui/button';
import { Download, ChevronDown } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { ReportDefinition, ReportColumn } from './reports.gql';
import { formatCellValue, exportToCsv, parseConditionalFormat } from './report-utils';
import { useTranslation } from 'react-i18next';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

/**
 * Evaluates a conditional format rule against a cell value.
 */
function evaluateCondition(cellValue: unknown, condition: string, ruleValue: unknown): boolean {
	if (cellValue == null) return false;
	const num = Number(cellValue);
	const ruleNum = Number(ruleValue);

	switch (condition) {
		case 'lt': return !isNaN(num) && !isNaN(ruleNum) && num < ruleNum;
		case 'lte': return !isNaN(num) && !isNaN(ruleNum) && num <= ruleNum;
		case 'gt': return !isNaN(num) && !isNaN(ruleNum) && num > ruleNum;
		case 'gte': return !isNaN(num) && !isNaN(ruleNum) && num >= ruleNum;
		case 'eq': return String(cellValue) === String(ruleValue);
		case 'neq': return String(cellValue) !== String(ruleValue);
		case 'contains': return String(cellValue).toLowerCase().includes(String(ruleValue).toLowerCase());
		default: return false;
	}
}

interface ReportDataTableProps {
	rows: Record<string, unknown>[];
	definition: ReportDefinition;
	isLoading: boolean;
	searchText: string;
	className?: string;
}

const DEFAULT_COL_DEF: ColDef = {
	sortable: true,
	resizable: true,
	lockVisible: true
} as const;

/**
 * Creates a valueFormatter closure for a report column based on its dataType.
 */
function createValueFormatter(col: ReportColumn, locale: string) {
	return (params: ValueFormatterParams) => {
		return formatCellValue(params.value, col, locale);
	};
}

/**
 * Maps a report column's alignment (from server) to a CSS class.
 */
function getAlignmentClass(col: ReportColumn): string {
	switch (col.alignment) {
		case 'RIGHT':
			return 'text-right';
		case 'CENTER':
			return 'text-center';
		case 'LEFT':
		default:
			return '';
	}
}

/**
 * Maps a report column's dataType to ag-Grid cell style for numeric alignment
 * and currency highlighting. Uses server-provided alignment when available.
 */
function getCellClass(col: ReportColumn): string | undefined {
	const alignment = getAlignmentClass(col);

	if (col.dataType === 'currency') {
		return `${alignment || 'text-right'} font-medium tabular-nums`;
	}

	const isNumeric =
		col.dataType === 'number' ||
		col.dataType === 'integer' ||
		col.dataType === 'decimal';

	if (isNumeric) {
		return `${alignment || 'text-right'} tabular-nums`;
	}

	return alignment || undefined;
}

/**
 * Maps a report column's dataType to an ag-Grid comparator for proper sorting.
 */
function getComparator(col: ReportColumn) {
	const isNumeric =
		col.dataType === 'number' ||
		col.dataType === 'integer' ||
		col.dataType === 'decimal' ||
		col.dataType === 'currency';
	const isDate = col.dataType === 'date' || col.dataType === 'datetime';

	if (isNumeric) {
		return (a: unknown, b: unknown) => {
			const na = a == null ? NaN : Number(a);
			const nb = b == null ? NaN : Number(b);
			if (isNaN(na) && isNaN(nb)) return 0;
			if (isNaN(na)) return -1;
			if (isNaN(nb)) return 1;
			return na - nb;
		};
	}
	if (isDate) {
		return (a: unknown, b: unknown) => {
			const da = a == null ? NaN : new Date(String(a)).getTime();
			const db = b == null ? NaN : new Date(String(b)).getTime();
			if (isNaN(da) && isNaN(db)) return 0;
			if (isNaN(da)) return -1;
			if (isNaN(db)) return 1;
			return da - db;
		};
	}
	return undefined; // Default string comparator
}

export function ReportDataTable({
	rows,
	definition,
	isLoading,
	searchText,
	className
}: ReportDataTableProps) {
	const { i18n } = useTranslation();
	const locale = i18n.language || 'en';
	const gridApiRef = useRef<GridApi | null>(null);

	// Convert ReportColumn definitions to ag-Grid ColDefs
	const columnDefs = useMemo<ColDef[]>(() => {
		return [...definition.columns]
			.sort((a, b) => a.sortOrder - b.sortOrder)
			.map(col => {
				const isRightAligned =
					col.alignment === 'RIGHT' ||
					(col.alignment === 'LEFT' &&
						(col.dataType === 'number' ||
							col.dataType === 'integer' ||
							col.dataType === 'decimal' ||
							col.dataType === 'currency'));

				const conditionalRules = parseConditionalFormat(col.conditionalFormatJson);

				const colDef: ColDef = {
					field: col.key,
					headerName: col.label,
					sortable: col.sortable,
					hide: !col.visibleByDefault,
					valueFormatter: createValueFormatter(col, locale),
					comparator: getComparator(col),
					cellClass: getCellClass(col),
					headerClass: isRightAligned ? 'ag-right-aligned-header' : undefined,
					minWidth: 100
				};

				// Apply width from server metadata
				if (col.width) {
					const pxMatch = col.width.match(/^(\d+)px$/);
					if (pxMatch) {
						colDef.width = parseInt(pxMatch[1], 10);
					} else {
						colDef.flex = 1;
					}
				} else {
					colDef.flex = 1;
				}

				// Apply conditional formatting as cellStyle
				if (conditionalRules.length > 0) {
					colDef.cellStyle = (params) => {
						for (const rule of conditionalRules) {
							if (evaluateCondition(params.value, rule.condition, rule.value)) {
								return rule.style;
							}
						}
						return undefined;
					};
				}

				return colDef;
			});
	}, [definition.columns, locale]);

	// Default sort model from definition
	const defaultSortModel = useMemo<SortModelItem[]>(() => {
		return definition.defaultSorts
			.sort((a, b) => a.sortOrder - b.sortOrder)
			.map(s => ({
				colId: s.columnKey,
				sort: (s.direction === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
			}));
	}, [definition.defaultSorts]);

	// Apply quick filter when searchText changes
	useEffect(() => {
		if (gridApiRef.current) {
			gridApiRef.current.setGridOption('quickFilterText', searchText || undefined);
		}
	}, [searchText]);



	const handleGridReady = useCallback(
		(params: { api: GridApi }) => {
			gridApiRef.current = params.api;

			// Apply default sort
			if (defaultSortModel.length > 0) {
				params.api.applyColumnState({
					state: defaultSortModel.map(s => ({
						colId: s.colId,
						sort: s.sort
					})),
					defaultState: { sort: null }
				});
			}
		},
		[defaultSortModel]
	);

	// Summary stats using server-provided aggregationType metadata
	const summaryInfo = useMemo(() => {
		const aggregatedCols = definition.columns.filter(c => c.aggregationType != null);

		const aggregations: { col: ReportColumn; label: string; value: number }[] = [];
		for (const col of aggregatedCols) {
			const values = rows
				.map(row => Number(row[col.key]))
				.filter(v => !isNaN(v));

			let result = 0;
			switch (col.aggregationType) {
				case 'SUM':
					result = values.reduce((s, v) => s + v, 0);
					break;
				case 'AVG':
					result = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
					break;
				case 'COUNT':
					result = values.length;
					break;
				case 'MIN':
					result = values.length > 0 ? Math.min(...values) : 0;
					break;
				case 'MAX':
					result = values.length > 0 ? Math.max(...values) : 0;
					break;
			}

			aggregations.push({
				col,
				label: col.aggregationType as string,
				value: result
			});
		}

		return { count: rows.length, aggregations };
	}, [rows, definition.columns]);

	const handleExport = useCallback(
		(format: string) => {
			if (format === 'csv') {
				const visibleCols = definition.columns
					.filter(c => c.visibleByDefault)
					.sort((a, b) => a.sortOrder - b.sortOrder);
				exportToCsv(
					rows,
					visibleCols,
					definition.title.replace(/\s+/g, '_'),
					locale
				);
			}
		},
		[rows, definition, locale]
	);

	return (
		<div className={`flex flex-col gap-4 ${className ?? ''}`}>
			{/* Summary + Toolbar */}
			<div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-3 shadow-sm">
				{/* Left: summary stats or result count */}
				{summaryInfo.aggregations.length > 0 && rows.length > 0 ? (
					<div className="flex items-center gap-4 text-sm text-muted-foreground">
						<span className="font-medium text-foreground">
							{rows.length} results
						</span>
						{summaryInfo.aggregations.map(agg => (
							<span key={agg.col.key}>
								{agg.label}({agg.col.label}):{' '}
								<span className="font-medium text-foreground">
									{formatCellValue(
										agg.value,
										agg.col,
										locale
									)}
								</span>
							</span>
						))}
					</div>
				) : (
					<div className="text-sm text-muted-foreground">
						{rows.length > 0
							? `${rows.length} results`
							: 'No results'}
					</div>
				)}

				{/* Right: export */}
				<div className="flex items-center gap-2">
					{definition.exportFormats.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5"
								>
									<Download className="h-4 w-4" />
									Export
									<ChevronDown className="h-3 w-3" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{definition.exportFormats.map(fmt => (
									<DropdownMenuItem
										key={fmt}
										onClick={() => handleExport(fmt)}
									>
										{fmt.toUpperCase()}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</div>

			{/* ag-Grid Table */}
			<div className="ag-theme-quartz vectro-ag-theme rounded-lg border border-border flex-1 min-h-0">
				<AgGridReact
					theme="legacy"
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={DEFAULT_COL_DEF}
					rowHeight={42}
					headerHeight={40}
					animateRows={true}
					loading={isLoading}
					suppressCellFocus
					suppressContextMenu
					domLayout="normal"
					overlayLoadingTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>Loading...</div></div>"
					overlayNoRowsTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>No results</div><div class='ag-custom-overlay__subtitle'>Try adjusting your filters or search criteria.</div></div>"
					onGridReady={handleGridReady}
				/>
			</div>
		</div>
	);
}
